import React, { useEffect, useState } from 'react';
import { WEBApi } from '../API/WEBApi';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Pagination, Modal, Button } from 'react-bootstrap';

interface Contact {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    message: string;
    propertyType: string;
    date: string;
}

const Contacts: React.FC = () => {
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
    const [loading, setLoading] = useState(false); // 🔄 loader state

    const fetchContacts = async (currentPage: number) => {
        setLoading(true); // Start loader
        try {
            const res = await WEBApi.getContacts(currentPage);
            setContacts(res.contacts);
            setTotalPages(res.totalPages);
        } catch (error) {
            console.error('Error fetching contacts:', error);
        } finally {
            setLoading(false); // Stop loader
        }
    };

    useEffect(() => {
        fetchContacts(page);
    }, [page]);

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this contact?')) return;
        try {
            await WEBApi.deleteContact(id);
            fetchContacts(page);
        } catch (error) {
            console.error('Error deleting contact:', error);
            alert('Failed to delete contact. Please try again.');
        }
    };

    const renderPagination = () => {
        let items = [];
        for (let number = 1; number <= totalPages; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === page}
                    onClick={() => setPage(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }
        return (
            <Pagination className="justify-content-end mt-4">
                <Pagination.Prev
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                />
                {items}
                <Pagination.Next
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                />
            </Pagination>
        );
    };

    const handleViewDetails = (contact: Contact) => {
        setSelectedContact(contact);
    };

    const handleCloseModal = () => {
        setSelectedContact(null);
    };

    return (
        <main className="container mt-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3>Inbound Tracker</h3>
            </div>

            {loading ? (
                <div className="text-center my-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            ) : (
                <>
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>Property Type</th>
                                <th>Date</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {contacts.map(contact => (
                                <tr key={contact._id}>
                                    <td>{contact.firstName} {contact.lastName}</td>
                                    <td>{contact.email}</td>
                                    <td>{contact.phone}</td>
                                    <td>{contact.propertyType}</td>
                                    <td>{new Date(contact.date).toLocaleDateString()}</td>
                                    <td>
                                        <button
                                            className="btn btn-sm btn-info me-2"
                                            onClick={() => handleViewDetails(contact)}
                                        >
                                            View
                                        </button>
                                        <button
                                            className="btn btn-sm btn-danger"
                                            onClick={() => handleDelete(contact._id)}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {renderPagination()}
                </>
            )}

            {/* Modal */}
            <Modal show={selectedContact !== null} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedContact && (
                        <div>
                            <p><strong>Name:</strong> {selectedContact.firstName} {selectedContact.lastName}</p>
                            <p><strong>Email:</strong> {selectedContact.email}</p>
                            <p><strong>Phone:</strong> {selectedContact.phone}</p>
                            <p><strong>Property Type:</strong> {selectedContact.propertyType}</p>
                            <p><strong>Date Added:</strong> {new Date(selectedContact.date).toLocaleDateString()}</p>
                            <p><strong>Message:</strong> {selectedContact.message}</p>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </main>
    );
};

export default Contacts;
